import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import PasswordInput from "./PasswordInput";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link");
      setIsValidating(false);
      return;
    }

    // Validate token
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/auth/validate-reset-token?token=${token}`,
      {
        method: "GET",
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setIsValidToken(data.valid);
        if (!data.valid) {
          setError("This reset link is invalid or has expired");
        }
      })
      .catch(() => {
        setError("Failed to validate reset link");
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const { newPassword, confirmPassword } = formData;

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            newPassword,
            confirmPassword,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <>
        <Breadcrumb title="Reset Password" />
        <div className="flex items-center justify-center bg-gray-50 px-4 py-12">
          <div className="max-w-md w-full bg-white rounded-xl shadow-md p-8 text-center">
            <p>Validating reset link...</p>
          </div>
        </div>
      </>
    );
  }

  if (!isValidToken) {
    return (
      <>
        <Breadcrumb title="Reset Password" />
        <div className="flex items-center justify-center bg-gray-50 px-4 py-12">
          <div className="max-w-md w-full bg-white rounded-xl shadow-md p-8">
            <div className="text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-red-600 mb-4">
                Invalid Reset Link
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Link
                to="/forgot-password"
                className="text-[#009fe3] hover:underline font-semibold"
              >
                Request a New Reset Link
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrumb title="Reset Password" />
      <div className="flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-[#009fe3]">
              Reset Your Password
            </h2>
            <p className="mt-2 text-gray-600">Enter your new password below</p>
          </div>

          {error && (
            <div className="mb-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-3 rounded">
              <p>{error}</p>
            </div>
          )}

          {success ? (
            <div className="text-center">
              <div className="mb-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded">
                <p className="font-semibold">✅ Password Reset Successful!</p>
                <p className="text-sm mt-2">
                  Your password has been reset. Redirecting to login...
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <PasswordInput
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  label="New Password"
                  placeholder="Enter new password"
                  showStrengthIndicator={true}
                  disabled={isSubmitting}
                />

                <br />

                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  label="Confirm New Password"
                  placeholder="Re-enter new password"
                  showStrengthIndicator={false}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 8 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white font-bold py-3 rounded-md transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </button>

              <div className="text-center mt-4">
                <Link
                  to="/login"
                  className="text-sm text-[#009fe3] hover:underline"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
